import { makeAutoObservable, observable } from 'mobx';
import { upperFirst } from 'lodash';
import { ModelValidator } from 'framework/mobx-integration';
import {
	AuthenticationFieldsStore,
	AuthenticationRecord,
	SimpleField,
} from 'areas/administration/authentication/types';

export class SimpleFieldsStore implements AuthenticationFieldsStore {
	model: Record<string, string>;
	stored: Record<string, boolean> = {};
	cleared: Record<string, boolean> = {};
	validator: ModelValidator<Record<string, string>>;

	constructor(
		public fields: SimpleField[],
		public readOnly: boolean = false,
	) {
		this.model = observable(Object.fromEntries(fields.map((field) => [field.name, field.defaultValue ?? ''])));
		this.validator = new ModelValidator(this.model);

		makeAutoObservable(this, {
			fields: false,
		});
	}

	load(record: Partial<AuthenticationRecord>) {
		this.fields.forEach((field) => {
			if (field.kind == 'secret') {
				this.stored[field.name] = field.clearable === false || !!record[`has${upperFirst(field.name)}`];
				return;
			}

			const value = record[field.name] as string | number;
			this.model[field.name] = value == null ? (field.defaultValue ?? '') : String(value);
		});
	}

	isClearable(field: SimpleField) {
		return field.kind == 'secret' && field.clearable !== false && this.stored[field.name] && !this.readOnly;
	}

	clear(field: SimpleField) {
		this.model[field.name] = '';
		this.stored[field.name] = false;
		this.cleared[field.name] = true;
	}

	get payload() {
		const result: Record<string, unknown> = {};

		this.fields.forEach((field) => {
			const value = this.model[field.name];

			if (field.kind == 'secret') {
				if (value) {
					result[field.name] = value;
				} else if (this.cleared[field.name]) {
					result[`clear${upperFirst(field.name)}`] = true;
				}
				return;
			}

			result[field.name] = field.kind == 'password' ? value : value.trim();
		});

		return result;
	}

	validate() {
		this.validator.validate();
		return this.validator.valid;
	}

	destroy() {
		this.validator.destroy();
	}
}
